import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import { useApi, useSession } from '../api/context';
import { LanguageSwitch } from '../components/Layout';
import { ZinqLogo } from '../components/ui';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/mk';
import { formatPhone, normalizeMkPhone } from '../lib/phone';

/** Dev / e2e builds only (VITE_SHOW_DEV_OTP=true): show the API's development code as a hint. */
export function showDevOtp(): boolean {
  return import.meta.env.VITE_SHOW_DEV_OTP === 'true';
}

function otpErrorKey(e: unknown, step: 'phone' | 'code'): MessageKey {
  if (e instanceof ApiError) {
    if (e.isNetwork) return 'error.network';
    // M5 pilot environment: only the study's prepared test numbers can sign in.
    if (e.code === 'PILOT_NUMBERS_ONLY') return 'login.pilotNumbersOnly';
    // POST /auth/otp/verify (M4 codes): the last two need a new code ("Испрати нов код").
    if (e.code === 'OTP_INVALID') return 'login.wrongCode';
    if (e.code === 'OTP_EXPIRED') return 'login.codeExpired';
    if (e.code === 'OTP_TOO_MANY_ATTEMPTS') return 'login.tooManyCodes';
    if (e.status === 429 || e.status === 403) return 'login.tooMany';
    if (step === 'code' && (e.status === 401 || e.status === 400)) return 'login.wrongCode';
    if (step === 'phone' && e.status === 400) return 'login.phoneInvalid';
  }
  return 'error.generic';
}

/** Sign-in with the phone number of the ZINQ business profile and an SMS code (OTP). */
export function LoginPage() {
  const { t } = useI18n();
  const api = useApi();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phoneInput, setPhoneInput] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [pilotCode, setPilotCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MessageKey | null>(() =>
    api.session.endReason === 'expired' ? 'error.session' : null,
  );
  const codeRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
    else phoneRef.current?.focus();
  }, [step]);

  if (session) return <Navigate to={from} replace />;

  const requestCode = async (target: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.requestOtp(target);
      setPhone(target);
      setDevCode(showDevOtp() && res.devCode ? res.devCode : null);
      // Pilot OTP mode: the API sends it for the study's test numbers only, in any build.
      setPilotCode(res.pilotCode && /^\d{6}$/.test(res.pilotCode) ? res.pilotCode : null);
      setCode('');
      setStep('code');
    } catch (e) {
      setError(otpErrorKey(e, 'phone'));
    } finally {
      setBusy(false);
    }
  };

  const onPhone = (e: FormEvent): void => {
    e.preventDefault();
    const normalized = normalizeMkPhone(phoneInput);
    if (!normalized) {
      setError('login.phoneInvalid');
      return;
    }
    void requestCode(normalized);
  };

  const onCode = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!phone) return;
    if (!/^\d{6}$/.test(code)) {
      setError('login.codeInvalid');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.verifyOtp(phone, code);
      navigate(from, { replace: true });
    } catch (err) {
      setError(otpErrorKey(err, 'code'));
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-top">
        <LanguageSwitch />
      </div>
      <main className="auth-card" id="main">
        <div className="auth-brand">
          <ZinqLogo size={44} />
          <div>
            <div className="brand-name">ZINQ</div>
            <div className="brand-sub">{t('app.panelName')}</div>
          </div>
        </div>
        <h1>{t('login.title')}</h1>
        {step === 'phone' ? (
          <form onSubmit={onPhone} noValidate>
            <p className="muted">{t('login.subtitle')}</p>
            <label className="field">
              <span className="field-label">{t('login.phone')}</span>
              <input
                ref={phoneRef}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                name="phone"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder={t('login.phoneHint')}
                aria-invalid={error === 'login.phoneInvalid' ? true : undefined}
                aria-describedby={error ? 'login-error' : undefined}
                required
              />
            </label>
            <button type="submit" className="btn btn--primary btn--block" disabled={busy} data-testid="login-send">
              {t('login.sendCode')}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void onCode(e)} noValidate>
            <p className="muted">{t('login.codeSentTo', { phone: formatPhone(phone ?? '') })}</p>
            <label className="field">
              <span className="field-label">{t('login.code')}</span>
              <input
                ref={codeRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                name="code"
                maxLength={6}
                pattern="\d{6}"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                aria-invalid={error === 'login.wrongCode' || error === 'login.codeInvalid' ? true : undefined}
                aria-describedby={error ? 'login-error' : undefined}
                required
              />
            </label>
            {pilotCode ? (
              <div className="pilot-code" role="status" data-testid="pilot-otp" data-code={pilotCode}>
                <span className="pilot-code-text">{t('login.pilotCode', { code: pilotCode })}</span>
                <button
                  type="button"
                  className="btn btn--secondary"
                  data-testid="pilot-otp-fill"
                  onClick={() => {
                    setCode(pilotCode);
                    setError(null);
                    codeRef.current?.focus();
                  }}
                >
                  {t('login.pilotFill')}
                </button>
              </div>
            ) : devCode ? (
              <p className="dev-hint" data-testid="dev-otp" data-code={devCode}>
                {t('login.devCode', { code: devCode })}
              </p>
            ) : null}
            <button type="submit" className="btn btn--primary btn--block" disabled={busy} data-testid="login-submit">
              {t('login.signIn')}
            </button>
            <div className="auth-links">
              <button
                type="button"
                className="btn btn--link"
                data-testid="login-change-phone"
                onClick={() => {
                  setStep('phone');
                  setError(null);
                  setDevCode(null);
                  setPilotCode(null);
                }}
              >
                {t('login.changePhone')}
              </button>
              <button
                type="button"
                className="btn btn--link"
                disabled={busy || !phone}
                onClick={() => phone && void requestCode(phone)}
              >
                {t('login.resend')}
              </button>
            </div>
          </form>
        )}
        {error ? (
          <p id="login-error" className="field-error" role="alert">
            {t(error)}
          </p>
        ) : null}
        <p className="auth-note">{t('login.noCustody')}</p>
      </main>
    </div>
  );
}
